import Fuse from "fuse.js";
import * as React from "react";
import { ActivityIndicator } from "../../components";
import {
  APPDATA_KEY,
  CURRENT_PAGE_KEY,
  CURRENT_REGION_KEY,
  SEARCH_TERM_KEY,
} from "../../data/constants";
import { ICountry, Region } from "../../data/types";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Box, Container, Typography } from "../../shared";
import Filter from "./Filter";
import List from "./List";
import Pagination from "./Pagination";

const PAGE_SIZE = 100;

interface CountriesProps {
  resetKey?: number;
}

const Countries: React.FC<CountriesProps> = ({ resetKey }) => {
  const [data, setData] = useLocalStorage<ICountry[]>(APPDATA_KEY, []);
  const [searchTerm, setSearchTerm] = useLocalStorage<string>(
    SEARCH_TERM_KEY,
    ""
  );
  const [currentRegion, setCurrentRegion] = useLocalStorage<Region>(
    CURRENT_REGION_KEY,
    Region.ALL
  );
  const [page, setPage] = useLocalStorage<number>(CURRENT_PAGE_KEY, 1);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const fetchAllCountries = async () => {
      try {
        let offset = 0;
        let allCountries: ICountry[] = [];
        let hasMore = true;

        while (hasMore) {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}?limit=${PAGE_SIZE}&offset=${offset}`,
            { headers: { Authorization: `Bearer ${process.env.REACT_APP_API_KEY}` } }
          );
          const result = await response.json();
          const countries = result.data.objects;
          allCountries = [...allCountries, ...countries];
          hasMore = result.data.meta.more;
          offset += PAGE_SIZE;
        }

        setData(allCountries);
      } catch (e) {
        setHasError(true);
      }
    };

    if (!data.length) fetchAllCountries();
  }, [data, setData]);

  React.useEffect(() => {
    setPage(1);
    setSearchTerm("");
    setCurrentRegion(Region.ALL);
  }, [resetKey]);

  const filterByRegion = (region: Region): ICountry[] => {
    if (region === Region.ALL) {
      return data;
    }
    return data.filter(
      (country) => country.region.toLowerCase() === region
    );
  };

  const filterBySearchTerm = (searchInput: string): ICountry[] => {
    let options: Fuse.IFuseOptions<ICountry> = {
      keys: ["names.common", "names.official", "region", "subregion", "capitals.name"],
    };

    let fuse = new Fuse<ICountry>(data, options);
    const result = fuse.search(searchInput);
    return result.map((v) => v.item);
  };

  const countriesPerPage = 12;
  let totalPages = 0;
  const paginate = (data: ICountry[]): ICountry[] => {
    totalPages = Math.ceil(data.length / countriesPerPage);
    const indexOfLast = page * countriesPerPage;
    const indexOfFirst = indexOfLast - countriesPerPage;
    return data.slice(indexOfFirst, indexOfLast);
  };

  const handleChangeRegion = (region: Region): void => {
    setPage(1);
    setSearchTerm("");
    setCurrentRegion(region);
  };

  const handleSearch = (text: string): void => {
    setPage(1);
    setSearchTerm(text);
  };

  const handlePrevious = () => {
    setPage((current) => {
      if (current > 1) {
        return current - 1;
      }
      return 1;
    });
  };

  const handleNext = () => {
    setPage((current) => {
      if (current < totalPages) {
        return current + 1;
      }
      return totalPages;
    });
  };

  if (!data.length) {
    return (
      <Container>
        <Box css={{ mt: "$8" }}>
          <ActivityIndicator />
        </Box>
      </Container>
    );
  }

  if (hasError) {
    return (
      <Container>
        <Box css={{ mt: "$8" }}>
          <Typography>"Error has occurred fetching data..."</Typography>
        </Box>
      </Container>
    );
  }

  const getAppState = () => {
    return {
      currentPage: page,
      currentRegion,
      searchTerm,
      setPage,
      setCurrentRegion,
      setSearchTerm,
    };
  };

  return (
    <Container>
      <Filter
        handleSearch={handleSearch}
        handleChangeRegion={handleChangeRegion}
        currentRegion={currentRegion}
      />
      <List
        {...getAppState()}
        countries={paginate(
          searchTerm.length
            ? filterBySearchTerm(searchTerm)
            : filterByRegion(currentRegion)
        )}
      />
      <Pagination
        handlePreviousPage={handlePrevious}
        handleNextPage={handleNext}
        page={page ?? 1}
        totalPages={totalPages}
      />
    </Container>
  );
};

export default Countries;
